import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { formatDate } from '../../../utils/dateUtils';

const PreviewModal = ({ isOpen, onClose, formData }) => {
  const [activeLanguage, setActiveLanguage] = useState('ar');

  if (!isOpen) return null;

  const languages = [
    { code: 'ar', label: 'العربية', flag: '🇸🇾', dir: 'rtl' },
    { code: 'en', label: 'English', flag: '🇺🇸', dir: 'ltr' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷', dir: 'ltr' }
  ];

  const getCurrentName = () => {
    const field = `name${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
    return formData?.[field] || 'اسم الاتحاد الفرعي';
  };

  const getCurrentDescription = () => {
    const field = `description${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
    return formData?.[field] || 'لا يوجد وصف متاح';
  };

  const getCityName = () => {
    const cities = {
      'damascus': 'دمشق - Damascus',
      'aleppo': 'حلب - Aleppo',
      'homs': 'حمص - Homs',
      'latakia': 'اللاذقية - Latakia',
      'hama': 'حماة - Hama'
    };
    return cities?.[formData?.cityId] || formData?.cityId || 'غير محدد';
  };

  const getStatusLabel = () => {
    const statuses = {
      'active': 'نشط - Active',
      'inactive': 'غير نشط - Inactive',
      'pending': 'في الانتظار - Pending',
      'suspended': 'معلق - Suspended'
    };
    return statuses?.[formData?.status] || 'غير محدد';
  };

  const getStatusColor = () => {
    const colors = {
      'active': 'text-success bg-success/10',
      'inactive': 'text-muted-foreground bg-muted',
      'pending': 'text-warning bg-warning/10',
      'suspended': 'text-error bg-error/10'
    };
    return colors?.[formData?.status] || 'text-muted-foreground bg-muted';
  };

  const getCurrentLanguage = () => {
    return languages?.find(lang => lang?.code === activeLanguage);
  };

  const primaryContact = formData?.contacts?.find(contact => contact?.isPrimary) || formData?.contacts?.[0];

  return (
    <div className="fixed inset-0 z-300 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-4xl bg-popover border border-border rounded-lg shadow-elevation-3 animate-slide-up max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Eye" size={18} color="white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-popover-foreground font-arabic">
                  معاينة الاتحاد الفرعي
                </h2>
                <p className="text-sm text-muted-foreground font-caption">
                  معاينة البيانات قبل النشر
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Language Selector */}
              <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
                {languages?.map((language) => (
                  <Button
                    key={language?.code}
                    variant={activeLanguage === language?.code ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveLanguage(language?.code)}
                  >
                    <span className="mr-1">{language?.flag}</span>
                    {language?.label}
                  </Button>
                ))}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                iconName="X"
                iconSize={20}
              />
            </div>
          </div>

          {/* Modal Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="p-6 space-y-6">
              {/* Header Section */}
              <div className="flex items-start space-x-6">
                {/* Logo */}
                <div className="flex-shrink-0">
                  {formData?.logo ? (
                    <Image
                      src={formData?.logo}
                      alt="Sub-union logo preview showing organization emblem with Syrian colors and Arabic calligraphy"
                      className="w-24 h-24 object-cover rounded-lg border border-border shadow-sm"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-muted rounded-lg flex items-center justify-center border border-border">
                      <Icon name="Building2" size={32} className="text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="flex-1 space-y-3" dir={getCurrentLanguage()?.dir}>
                  <div>
                    <h1 className={`text-2xl font-bold text-foreground font-arabic ${
                      getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
                    }`}>
                      {getCurrentName()}
                    </h1>
                    <div className="flex items-center space-x-2 mt-2">
                      <Icon name="MapPin" size={16} className="text-muted-foreground" />
                      <span className="text-muted-foreground font-caption">
                        {getCityName()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()}`}>
                      {getStatusLabel()}
                    </span>
                    
                    {formData?.establishedDate && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Calendar" size={14} />
                        <span className="font-caption">
                          تأسس في {formatDate(formData.establishedDate)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground font-arabic">
                  الوصف
                </h3>
                <div 
                  className={`prose prose-sm max-w-none text-foreground ${
                    getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
                  }`}
                  dir={getCurrentLanguage()?.dir}
                >
                  <div className="whitespace-pre-wrap bg-muted/30 rounded-lg p-4">
                    {getCurrentDescription()}
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name="Users" size={20} className="text-success" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {formData?.totalMembers || '0'}
                      </p>
                      <p className="text-sm text-muted-foreground font-caption">
                        إجمالي الأعضاء
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon name="UserCheck" size={20} className="text-accent" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {formData?.activeMembers || '0'}
                      </p>
                      <p className="text-sm text-muted-foreground font-caption">
                        الأعضاء النشطون
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                      <Icon name="UserPlus" size={20} className="text-warning" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {formData?.newMembersThisMonth || '0'}
                      </p>
                      <p className="text-sm text-muted-foreground font-caption">
                        أعضاء جدد هذا الشهر
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              {(formData?.contacts?.length > 0 || primaryContact) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    معلومات الاتصال
                  </h3>
                  
                  <div className="space-y-3">
                    {formData?.contacts?.map((contact, index) => (
                      <div
                        key={index}
                        className={`bg-card border rounded-lg p-4 ${
                          contact?.isPrimary ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-foreground">
                                {contact?.name}
                              </h4>
                              {contact?.isPrimary && (
                                <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full font-caption">
                                  رئيسي
                                </span>
                              )}
                            </div>
                            
                            {contact?.position && (
                              <p className="text-sm text-muted-foreground font-caption">
                                {contact?.position}
                              </p>
                            )}
                            
                            <div className="flex flex-wrap gap-4 text-sm">
                              <div className="flex items-center space-x-2">
                                <Icon name="Mail" size={14} className="text-muted-foreground" />
                                <span className="font-caption">{contact?.email}</span>
                              </div>
                              
                              {contact?.phone && (
                                <div className="flex items-center space-x-2">
                                  <Icon name="Phone" size={14} className="text-muted-foreground" />
                                  <span className="font-caption">{contact?.phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Information Details */}
              {(formData?.phone || formData?.contactEmail || formData?.contactNumberAr || formData?.contactNumberEn || formData?.contactNumberTr) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    معلومات الاتصال التفصيلية
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData?.phone && (
                      <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">رقم الهاتف</p>
                          <p className="text-sm text-muted-foreground font-caption">
                            {formData?.phone}
                          </p>
                        </div>
                      </div>
                    )}

                    {formData?.contactEmail && (
                      <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Mail" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">البريد الإلكتروني</p>
                          <p className="text-sm text-muted-foreground font-caption">
                            {formData?.contactEmail}
                          </p>
                        </div>
                      </div>
                    )}

                    {formData?.contactNumberAr && (
                      <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">رقم الاتصال (عربي)</p>
                          <p className="text-sm text-muted-foreground font-caption">
                            {formData?.contactNumberAr}
                          </p>
                        </div>
                      </div>
                    )}

                    {formData?.contactNumberEn && (
                      <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">Contact Number (English)</p>
                          <p className="text-sm text-muted-foreground font-caption">
                            {formData?.contactNumberEn}
                          </p>
                        </div>
                      </div>
                    )}

                    {formData?.contactNumberTr && (
                      <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">İletişim Numarası (Türkçe)</p>
                          <p className="text-sm text-muted-foreground font-caption">
                            {formData?.contactNumberTr}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Information */}
              {(formData?.website || formData?.facebookPage || formData?.registrationNumber) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    معلومات إضافية
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData?.website && (
                      <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Globe" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">الموقع الإلكتروني</p>
                          <a 
                            href={formData?.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-accent hover:underline font-caption"
                          >
                            {formData?.website}
                          </a>
                        </div>
                      </div>
                    )}

                    {formData?.facebookPage && (
                      <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Facebook" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">صفحة الفيسبوك</p>
                          <a 
                            href={formData?.facebookPage} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-accent hover:underline font-caption"
                          >
                            {formData?.facebookPage}
                          </a>
                        </div>
                      </div>
                    )}

                    {formData?.registrationNumber && (
                      <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="FileText" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">رقم التسجيل</p>
                          <p className="text-sm text-muted-foreground font-caption">
                            {formData?.registrationNumber}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Card Description */}
              {(formData?.cardDescriptionAr || formData?.cardDescriptionEn || formData?.cardDescriptionTr) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    وصف البطاقة
                  </h3>
                  <div 
                    className={`prose prose-sm max-w-none text-foreground ${
                      getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
                    }`}
                    dir={getCurrentLanguage()?.dir}
                  >
                    <div className="whitespace-pre-wrap bg-muted/30 rounded-lg p-4">
                      {(() => {
                        const field = `cardDescription${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
                        return formData?.[field] || 'لا يوجد محتوى';
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* About Description */}
              {(formData?.aboutDescriptionAr || formData?.aboutDescriptionEn || formData?.aboutDescriptionTr) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    صفحة من نحن
                  </h3>
                  <div 
                    className={`prose prose-sm max-w-none text-foreground ${
                      getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
                    }`}
                    dir={getCurrentLanguage()?.dir}
                  >
                    <div className="whitespace-pre-wrap bg-muted/30 rounded-lg p-4">
                      {(() => {
                        const field = `aboutDescription${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
                        return formData?.[field] || 'لا يوجد محتوى';
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Special Achievements */}
              {(formData?.specialAchievementsAr || formData?.specialAchievementsEn || formData?.specialAchievementsTr) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    الإنجازات الخاصة
                  </h3>
                  <div 
                    className={`prose prose-sm max-w-none text-foreground ${
                      getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
                    }`}
                    dir={getCurrentLanguage()?.dir}
                  >
                    <div className="whitespace-pre-wrap bg-muted/30 rounded-lg p-4">
                      {(() => {
                        const field = `specialAchievements${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
                        return formData?.[field] || 'لا يوجد محتوى';
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Activities and Achievements */}
              {(formData?.activitiesAndAchievementsAr || formData?.activitiesAndAchievementsEn || formData?.activitiesAndAchievementsTr) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    الأنشطة والإنجازات
                  </h3>
                  <div 
                    className={`prose prose-sm max-w-none text-foreground ${
                      getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
                    }`}
                    dir={getCurrentLanguage()?.dir}
                  >
                    <div className="whitespace-pre-wrap bg-muted/30 rounded-lg p-4">
                      {(() => {
                        const field = `activitiesAndAchievements${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
                        return formData?.[field] || 'لا يوجد محتوى';
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Current Administrative Team */}
              {(formData?.currentAdministrativeTeamAr || formData?.currentAdministrativeTeamEn || formData?.currentAdministrativeTeamTr) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    الفريق الإداري الحالي
                  </h3>
                  <div 
                    className={`prose prose-sm max-w-none text-foreground ${
                      getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
                    }`}
                    dir={getCurrentLanguage()?.dir}
                  >
                    <div className="whitespace-pre-wrap bg-muted/30 rounded-lg p-4">
                      {(() => {
                        const field = `currentAdministrativeTeam${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
                        return formData?.[field] || 'لا يوجد محتوى';
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {(formData?.notesAr || formData?.notesEn || formData?.notesTr) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground font-arabic">
                    ملاحظات
                  </h3>
                  <div 
                    className={`prose prose-sm max-w-none text-foreground ${
                      getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
                    }`}
                    dir={getCurrentLanguage()?.dir}
                  >
                    <div className="whitespace-pre-wrap bg-muted/30 rounded-lg p-4">
                      {(() => {
                        const field = `notes${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
                        return formData?.[field] || 'لا يوجد محتوى';
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
            <div className="text-sm text-muted-foreground font-caption">
              معاينة • {getCurrentLanguage()?.label} {getCurrentLanguage()?.flag}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={onClose}
                iconName="X"
                iconPosition="left"
                iconSize={16}
              >
                إغلاق
              </Button>
              
              <Button
                variant="default"
                onClick={() => {
                  onClose();
                  // Here you would typically trigger the publish action
                }}
                iconName="Send"
                iconPosition="left"
                iconSize={16}
              >
                نشر الآن
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Click outside to close */}
      <div 
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>
  );
};

export default PreviewModal;